"use client";

// Interactive product-tour ("onboarding buddy") — spotlight + popover that
// walks a fresh user through the app right after the curated replay finishes.
// Custom-built rather than a library so the look matches the rest of Xynth
// and we have full control over framer-motion-driven motion between anchors.
//
// Usage:
//   1. Wrap the app with <OnboardingTourProvider /> (already mounted in
//      app/layout.tsx). Mounts the global overlay portal.
//   2. Decorate the elements you want to point at with `data-tour="my-id"`.
//   3. From anywhere, call `useTour().start([...steps], { onComplete })`.
//
// Each step has a CSS selector for its anchor (typically
// `[data-tour="my-id"]`) plus a title + body. The provider handles
// measurement, smooth motion between anchors, scroll-into-view, viewport
// clamping, and a final no-anchor "centered" step (when `target` is null).

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { createPortal } from "react-dom";
import { AnimatePresence, motion } from "framer-motion";
import { ChevronRight, Sparkles, X } from "lucide-react";
import { cn } from "@/lib/utils";

// ── Types ────────────────────────────────────────────────────────────────────

export interface TourStep {
  id: string;
  /** CSS selector for the anchor element. Pass `null` for a centered step
   *  (e.g. final CTA where there's nothing on the page to point at). */
  target: string | null;
  title: string;
  body: string;
  /** Where to place the popover relative to the anchor. Defaults to "auto"
   *  which picks the side with the most viewport room. */
  placement?: "top" | "bottom" | "left" | "right" | "auto";
  /** Override the CTA button label. Defaults to "Next" or "Done" on last. */
  cta?: string;
  /** Fired BEFORE advancing to the next step. Use for side-effects like
   *  opening a panel that the next step needs to anchor on. */
  onCta?: () => void;
}

/** "Idle" mode: a small persistent bubble pinned to the bottom-right corner.
 *  Used while the user is on a passive surface (e.g. watching the curated
 *  onboarding replay) where the tour hasn't started yet but we want the
 *  buddy on-screen so the experience feels continuous. The same DOM element
 *  morphs from idle pill to active spotlight popover via framer-motion's
 *  shared `layoutId` — no flash, no remount. */
export interface TourIdleMessage {
  title: string;
  body?: string;
}

interface TourContextValue {
  isActive: boolean;
  isIdle: boolean;
  idleMessage: TourIdleMessage | null;
  currentStep: TourStep | null;
  currentIndex: number;
  total: number;
  start: (
    steps: TourStep[],
    opts?: { onComplete?: () => void; onSkip?: () => void },
  ) => void;
  startIdle: (message: TourIdleMessage) => void;
  endIdle: () => void;
  next: () => void;
  skip: () => void;
}

const TourContext = createContext<TourContextValue | null>(null);

export function useTour(): TourContextValue {
  const ctx = useContext(TourContext);
  // Inert fallback so children of the tree never crash if the provider
  // somehow isn't mounted (e.g. during a stale Next dev hot-reload).
  if (!ctx) {
    return {
      isActive: false,
      isIdle: false,
      idleMessage: null,
      currentStep: null,
      currentIndex: 0,
      total: 0,
      start: () => {},
      startIdle: () => {},
      endIdle: () => {},
      next: () => {},
      skip: () => {},
    };
  }
  return ctx;
}

// ── Provider ─────────────────────────────────────────────────────────────────

export function OnboardingTourProvider({ children }: { children: ReactNode }) {
  const [steps, setSteps] = useState<TourStep[]>([]);
  const [index, setIndex] = useState(0);
  const [idleMessage, setIdleMessage] = useState<TourIdleMessage | null>(null);
  const completeCb = useRef<(() => void) | null>(null);
  const skipCb = useRef<(() => void) | null>(null);

  const isActive = steps.length > 0;
  const isIdle = !isActive && idleMessage !== null;
  const currentStep = isActive ? steps[index] : null;

  const start = useCallback<TourContextValue["start"]>((newSteps, opts) => {
    completeCb.current = opts?.onComplete ?? null;
    skipCb.current = opts?.onSkip ?? null;
    setIndex(0);
    setSteps(newSteps);
    // Tour supersedes idle — clear the idle message so the same buddy
    // element morphs from corner pill to spotlight popover via shared
    // layoutId rather than rendering two competing buddies.
    setIdleMessage(null);
  }, []);

  const startIdle = useCallback<TourContextValue["startIdle"]>((message) => {
    setIdleMessage(message);
  }, []);

  const endIdle = useCallback(() => {
    setIdleMessage(null);
  }, []);

  const next = useCallback(() => {
    setIndex((prev) => {
      // Run the current step's onCta side-effect (e.g. open a panel) BEFORE
      // measuring the next step's anchor.
      steps[prev]?.onCta?.();
      const nextIdx = prev + 1;
      if (nextIdx >= steps.length) {
        setSteps([]);
        completeCb.current?.();
        return 0;
      }
      return nextIdx;
    });
  }, [steps]);

  const skip = useCallback(() => {
    setSteps([]);
    setIndex(0);
    skipCb.current?.();
  }, []);

  const value: TourContextValue = {
    isActive,
    isIdle,
    idleMessage,
    currentStep,
    currentIndex: index,
    total: steps.length,
    start,
    startIdle,
    endIdle,
    next,
    skip,
  };

  return (
    <TourContext.Provider value={value}>
      {children}
      <OnboardingTourOverlay />
    </TourContext.Provider>
  );
}

// ── Overlay ──────────────────────────────────────────────────────────────────

const POPOVER_W = 320;
const POPOVER_H_EST = 168; // rough — used for bottom-edge clamping
const POPOVER_OFFSET = 16;
const SPOTLIGHT_PAD = 8;
const SPOTLIGHT_RX = 12;

function OnboardingTourOverlay() {
  const {
    isActive,
    isIdle,
    idleMessage,
    currentStep,
    currentIndex,
    total,
    next,
    skip,
  } = useTour();
  const [rect, setRect] = useState<DOMRect | null>(null);
  const [vp, setVp] = useState({ w: 0, h: 0 });
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    setMounted(true);
  }, []);

  // Continuously remeasure the anchor: covers smooth-scrolling settling, sidebar
  // animations, and async DOM mounts. 200ms is invisible to the eye and cheap.
  useEffect(() => {
    if (!isActive || !currentStep) {
      setRect(null);
      return;
    }
    let scrolled = false;
    const measure = () => {
      setVp({ w: window.innerWidth, h: window.innerHeight });
      if (!currentStep.target) {
        setRect(null);
        return;
      }
      const el = document.querySelector(
        currentStep.target,
      ) as HTMLElement | null;
      if (!el) {
        setRect(null);
        return;
      }
      const r = el.getBoundingClientRect();
      setRect(r);
      if (!scrolled) {
        const inView = r.top >= 0 && r.bottom <= window.innerHeight;
        if (!inView) {
          el.scrollIntoView({ behavior: "smooth", block: "center" });
        }
        scrolled = true;
      }
    };
    measure();
    const id = window.setInterval(measure, 200);
    window.addEventListener("resize", measure);
    return () => {
      window.clearInterval(id);
      window.removeEventListener("resize", measure);
    };
  }, [isActive, currentStep]);

  // Esc to skip — hot-key for power users who want out fast.
  useEffect(() => {
    if (!isActive) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") skip();
      else if (e.key === "Enter") next();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [isActive, next, skip]);

  if (!mounted) return null;

  // ── IDLE MODE ────────────────────────────────────────────────────────────
  // When the buddy is in idle mode and no tour is active, render a small
  // pinned pill in the bottom-right corner. Uses the SAME `layoutId` as the
  // active popover so transitioning into the tour morphs the bubble in
  // place (no flash, no remount).
  if (!isActive) {
    if (!isIdle || !idleMessage) return null;
    return createPortal(
      <div
        className="fixed inset-0 z-[100] pointer-events-none"
        aria-live="polite"
      >
        <motion.div
          layoutId="onboarding-buddy"
          className="fixed bottom-6 right-6 pointer-events-auto"
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: 12 }}
          transition={{ type: "spring", stiffness: 260, damping: 26 }}
        >
          <div className="flex items-center gap-2.5 rounded-full pl-2 pr-3.5 py-1.5 bg-zinc-900/95 backdrop-blur-xl border border-white/10 shadow-[0_12px_36px_rgba(0,0,0,0.45)] text-zinc-100">
            <div className="flex-shrink-0 size-7 rounded-full bg-gradient-to-br from-indigo-500/40 to-purple-500/40 border border-white/10 flex items-center justify-center">
              <Sparkles className="size-3.5 text-indigo-200" />
            </div>
            <div className="min-w-0">
              <div className="text-xs font-medium leading-tight">
                {idleMessage.title}
              </div>
              {idleMessage.body && (
                <div className="text-[10px] text-zinc-400 leading-tight mt-0.5">
                  {renderTourBody(idleMessage.body)}
                </div>
              )}
            </div>
          </div>
        </motion.div>
      </div>,
      document.body,
    );
  }

  if (!currentStep) return null;

  const isLast = currentIndex === total - 1;
  const ctaLabel = currentStep.cta ?? (isLast ? "Done" : "Next");

  // Compute popover position. When we have an anchor we pick a side; without
  // one we center the popover near the bottom of the viewport.
  let popLeft = (vp.w - POPOVER_W) / 2;
  let popTop = vp.h - POPOVER_H_EST - 32;
  if (rect) {
    const placement =
      currentStep.placement === "auto" || !currentStep.placement
        ? autoPlacement(rect, vp)
        : currentStep.placement;
    if (placement === "right") {
      popLeft = rect.right + POPOVER_OFFSET;
      popTop = rect.top + rect.height / 2 - POPOVER_H_EST / 2;
    } else if (placement === "left") {
      popLeft = rect.left - POPOVER_W - POPOVER_OFFSET;
      popTop = rect.top + rect.height / 2 - POPOVER_H_EST / 2;
    } else if (placement === "top") {
      popLeft = rect.left + rect.width / 2 - POPOVER_W / 2;
      popTop = rect.top - POPOVER_H_EST - POPOVER_OFFSET;
    } else {
      popLeft = rect.left + rect.width / 2 - POPOVER_W / 2;
      popTop = rect.bottom + POPOVER_OFFSET;
    }
    popLeft = Math.max(16, Math.min(vp.w - POPOVER_W - 16, popLeft));
    popTop = Math.max(16, Math.min(vp.h - POPOVER_H_EST - 16, popTop));
  }

  return createPortal(
    <div
      className="fixed inset-0 z-[100] pointer-events-none"
      aria-live="polite"
    >
      {/* Spotlight backdrop. SVG mask cuts a rounded hole around the anchor;
          the rest of the page is dimmed. When `rect` is null (centered step)
          we just dim the whole viewport. */}
      <motion.svg
        className="absolute inset-0 w-full h-full pointer-events-auto"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.25 }}
        // Block accidental clicks on the dimmed area.
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
        }}
      >
        <defs>
          <mask id="xynth-tour-mask">
            <rect width="100%" height="100%" fill="white" />
            {rect && (
              <motion.rect
                fill="black"
                rx={SPOTLIGHT_RX}
                initial={false}
                animate={{
                  x: rect.left - SPOTLIGHT_PAD,
                  y: rect.top - SPOTLIGHT_PAD,
                  width: rect.width + SPOTLIGHT_PAD * 2,
                  height: rect.height + SPOTLIGHT_PAD * 2,
                }}
                transition={{ type: "spring", stiffness: 220, damping: 28 }}
              />
            )}
          </mask>
        </defs>
        <rect
          width="100%"
          height="100%"
          fill="rgba(0,0,0,0.6)"
          mask="url(#xynth-tour-mask)"
        />
        {/* Soft glow around the spotlight to draw the eye. */}
        {rect && (
          <motion.rect
            initial={false}
            animate={{
              x: rect.left - SPOTLIGHT_PAD,
              y: rect.top - SPOTLIGHT_PAD,
              width: rect.width + SPOTLIGHT_PAD * 2,
              height: rect.height + SPOTLIGHT_PAD * 2,
            }}
            transition={{ type: "spring", stiffness: 220, damping: 28 }}
            rx={SPOTLIGHT_RX}
            fill="none"
            stroke="rgba(255,255,255,0.45)"
            strokeWidth={1.5}
          />
        )}
      </motion.svg>

      {/* Popover — a SINGLE persistent element that slides between anchor
          positions. framer-motion interpolates the `left`/`top` props so the
          buddy "hops" smoothly from step to step instead of disappearing and
          reappearing. Shares `layoutId` with the idle pill so transitioning
          INTO the tour morphs the same DOM node (corner pill expands in
          place into the spotlight popover). */}
      <motion.div
        layoutId="onboarding-buddy"
        className="absolute pointer-events-auto"
        initial={{ opacity: 0, scale: 0.92, left: popLeft, top: popTop }}
        animate={{
          opacity: 1,
          scale: 1,
          left: popLeft,
          top: popTop,
        }}
        transition={{ type: "spring", stiffness: 260, damping: 28 }}
        style={{ width: POPOVER_W }}
      >
        <div className="relative rounded-2xl bg-zinc-900/95 backdrop-blur-xl border border-white/10 shadow-[0_20px_60px_rgba(0,0,0,0.5)] p-5 text-zinc-100 overflow-hidden">
          <AnimatePresence mode="wait">
            <motion.div
              key={currentStep.id}
              initial={{ opacity: 0, y: 6 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -6 }}
              transition={{ duration: 0.18 }}
            >
              <div className="flex items-start gap-3">
                <div className="flex-shrink-0 size-9 rounded-full bg-gradient-to-br from-indigo-500/30 to-purple-500/30 border border-white/10 flex items-center justify-center">
                  <Sparkles className="size-4 text-indigo-300" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-semibold leading-snug">
                    {currentStep.title}
                  </div>
                  <div className="mt-1 text-xs text-zinc-400 leading-relaxed">
                    {renderTourBody(currentStep.body)}
                  </div>
                </div>
                <button
                  onClick={skip}
                  className="flex-shrink-0 -mr-1 -mt-1 p-1 rounded-md text-zinc-500 hover:text-zinc-200 hover:bg-white/5 transition-colors"
                  aria-label="Skip tour"
                >
                  <X className="size-3.5" />
                </button>
              </div>
            </motion.div>
          </AnimatePresence>

          <div className="mt-4 flex items-center justify-between">
            <div className="flex items-center gap-1">
              {Array.from({ length: total }).map((_, i) => (
                <div
                  key={i}
                  className={cn(
                    "h-1 rounded-full transition-all duration-200",
                    i === currentIndex
                      ? "w-5 bg-white"
                      : i < currentIndex
                        ? "w-1.5 bg-white/50"
                        : "w-1.5 bg-white/15",
                  )}
                />
              ))}
            </div>
            <button
              onClick={next}
              className="flex items-center gap-1 px-3 py-1.5 text-xs font-medium bg-white text-black rounded-lg hover:bg-zinc-200 transition-colors"
            >
              {ctaLabel}
              <ChevronRight className="size-3" />
            </button>
          </div>
        </div>
      </motion.div>
    </div>,
    document.body,
  );
}

function autoPlacement(
  rect: DOMRect,
  vp: { w: number; h: number },
): "top" | "bottom" | "left" | "right" {
  const spaceBelow = vp.h - rect.bottom;
  const spaceAbove = rect.top;
  const spaceRight = vp.w - rect.right;
  const spaceLeft = rect.left;
  // Prefer below > right > above > left so the popover doesn't fight with
  // the bottom replay-controls bar (which sits at the very bottom edge).
  if (spaceBelow >= POPOVER_H_EST + 32) return "bottom";
  if (spaceRight >= POPOVER_W + 32) return "right";
  if (spaceAbove >= POPOVER_H_EST + 32) return "top";
  if (spaceLeft >= POPOVER_W + 32) return "left";
  return "bottom";
}

// ── Lightweight body renderer ────────────────────────────────────────────────
// Steps' `body` strings support a tiny markdown subset:
//   • `**text**`  → bold
//   • `\n\n`      → paragraph break (mt-2 spacing)
// Mirrors the renderer in `components/buddy/BuddyOverlay.tsx` so platform
// tour copy and replay buddy beats look identical. Intentionally NOT a
// full markdown parser — keeps `TourStep.body` a plain string so authors
// (and any future server-driven step source) don't need to build React nodes.
function renderTourBody(body: string): ReactNode {
  const paragraphs = body.split(/\n{2,}/);
  return paragraphs.map((para, i) => (
    <p key={i} className={i > 0 ? "mt-2" : undefined}>
      {renderInlineBold(para)}
    </p>
  ));
}

function renderInlineBold(text: string): ReactNode[] {
  // Split on `**...**` keeping the capture. Odd-indexed chunks are the
  // bold inner text; even-indexed are plain. Double-asterisk required
  // (not single) so mid-word asterisks in tickers / math don't trigger.
  const parts = text.split(/\*\*([^*]+)\*\*/g);
  return parts.map((part, i) =>
    i % 2 === 1 ? (
      <strong key={i} className="font-semibold text-white">
        {part}
      </strong>
    ) : (
      <span key={i}>{part}</span>
    ),
  );
}
